/**
 * 日誌服務
 * 提供更好的 console.log 機制，只在 DEBUG MODE 啟用時才顯示日誌
 */

// 從環境變數中獲取 DEBUG 模式設定
const isDebugMode = import.meta.env.VITE_DEBUG === "true";

// 日誌級別
export enum LogLevel {
  DEBUG = "debug",
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}

// 日誌顏色配置
const LOG_COLORS = {
  [LogLevel.DEBUG]: "#9575cd", // 紫色
  [LogLevel.INFO]: "#4fc3f7", // 藍色
  [LogLevel.WARN]: "#ffa726", // 橙色
  [LogLevel.ERROR]: "#ef5350", // 紅色
};

// 日誌前綴
const LOG_PREFIX = {
  [LogLevel.DEBUG]: "🔍 DEBUG",
  [LogLevel.INFO]: "📌 INFO",
  [LogLevel.WARN]: "⚠️ WARN",
  [LogLevel.ERROR]: "❌ ERROR",
};

/**
 * 創建格式化的日誌訊息
 * @param level 日誌級別
 * @param module 模組名稱
 * @param message 日誌訊息
 * @returns 格式化的日誌訊息
 */
const formatLogMessage = (
  level: LogLevel,
  module: string,
  message: string,
): string => {
  const timestamp = new Date().toISOString().split("T")[1].split(".")[0];
  return `[${timestamp}] ${LOG_PREFIX[level]} [${module}] ${message}`;
};

/**
 * 日誌服務
 */
export const logger = {
  /**
   * 創建特定模組的日誌器
   * @param module 模組名稱
   * @returns 模組特定的日誌器
   */
  createLogger: (module: string) => {
    return {
      /**
       * 調試級別日誌
       * @param message 日誌訊息
       * @param data 額外數據
       */
      debug: (message: string, ...data: any[]) => {
        if (isDebugMode) {
          console.log(
            `%c${formatLogMessage(LogLevel.DEBUG, module, message)}`,
            `color: ${LOG_COLORS[LogLevel.DEBUG]}; font-weight: bold`,
            ...data,
          );
        }
      },

      /**
       * 信息級別日誌
       * @param message 日誌訊息
       * @param data 額外數據
       */
      info: (message: string, ...data: any[]) => {
        if (isDebugMode) {
          console.log(
            `%c${formatLogMessage(LogLevel.INFO, module, message)}`,
            `color: ${LOG_COLORS[LogLevel.INFO]}; font-weight: bold`,
            ...data,
          );
        }
      },

      /**
       * 警告級別日誌
       * @param message 日誌訊息
       * @param data 額外數據
       */
      warn: (message: string, ...data: any[]) => {
        if (isDebugMode) {
          console.warn(
            `%c${formatLogMessage(LogLevel.WARN, module, message)}`,
            `color: ${LOG_COLORS[LogLevel.WARN]}; font-weight: bold`,
            ...data,
          );
        }
      },

      /**
       * 錯誤級別日誌
       * @param message 日誌訊息
       * @param data 額外數據
       */
      error: (message: string, ...data: any[]) => {
        // 錯誤日誌總是顯示，即使在非調試模式
        console.error(
          `%c${formatLogMessage(LogLevel.ERROR, module, message)}`,
          `color: ${LOG_COLORS[LogLevel.ERROR]}; font-weight: bold`,
          ...data,
        );
      },

      /**
       * 分組日誌
       * @param groupName 分組名稱
       * @param callback 分組內容回調
       */
      group: (groupName: string, callback: () => void) => {
        if (isDebugMode) {
          console.group(
            `%c${LOG_PREFIX[LogLevel.DEBUG]} [${module}] ${groupName}`,
            `color: ${LOG_COLORS[LogLevel.DEBUG]}; font-weight: bold`,
          );
          callback();
          console.groupEnd();
        }
      },

      /**
       * 計時日誌
       * @param label 計時標籤
       * @param callback 計時內容回調
       */
      time: async <T>(
        label: string,
        callback: () => Promise<T> | T,
      ): Promise<T> => {
        if (!isDebugMode) {
          return await callback();
        }

        const startTime = performance.now();
        try {
          const result = await callback();
          const endTime = performance.now();
          console.log(
            `%c${formatLogMessage(LogLevel.DEBUG, module, `${label} - ${(endTime - startTime).toFixed(2)}ms`)}`,
            `color: ${LOG_COLORS[LogLevel.DEBUG]}; font-weight: bold`,
          );
          return result;
        } catch (error) {
          const endTime = performance.now();
          console.error(
            `%c${formatLogMessage(LogLevel.ERROR, module, `${label} - ${(endTime - startTime).toFixed(2)}ms - Failed`)}`,
            `color: ${LOG_COLORS[LogLevel.ERROR]}; font-weight: bold`,
            error,
          );
          throw error;
        }
      },
    };
  },
};
